import { Suspense, useEffect } from 'react';
import { JSXElement } from '@/common/types';
import { RouteGuard } from '@/components/authentication-guard';
import { Loading } from '@/components/loading';
import { JobViewerLayout } from './job-viewer-layout';
import { ErrorBoundary } from '@/components/error-boundary';
import './style.css';
import { CloudAuthProvider, useCloudAuth } from '@itential/cloud-auth-client';
import { env } from '@/utils/shared/env';
import { useSearchParams, Outlet } from 'react-router';
import { RouteType } from '@/components/authentication-guard/types';
import { EnvironmentProvider } from '@/contexts/environment-context';
import { setRaygunUser, clearRaygunUser } from '@/services/monitoring';
import { hashString } from '@/utils/shared/hash';
import { AuthStateInitializer } from '@/components/auth-state-initializer';

export const ProtectedRouteLayout = (): JSXElement => {
    return (
        <RouteGuard routeType={RouteType.PRIVATE}>
            <JobViewerLayout>
                <Suspense fallback={<Loading />}>
                    <ErrorBoundary>
                        <Outlet />
                    </ErrorBoundary>
                </Suspense>
            </JobViewerLayout>
        </RouteGuard>
    );
};

export const PublicRouteLayout = (): JSXElement => {
    return (
        <RouteGuard routeType={RouteType.PUBLIC}>
            <Outlet />
        </RouteGuard>
    );
};

export const AuthRouteLayout = (): JSXElement => {
    return (
        <RouteGuard routeType={RouteType.AUTH}>
            <Outlet />
        </RouteGuard>
    );
};

function RaygunUserTracker(): null {
    const { state } = useCloudAuth();

    useEffect(() => {
        if (state.state === 'signed-in') {
            const { realm, email } = state;
            hashString(email).then((hashedEmail) => {
                setRaygunUser({
                    id: `${realm}:${hashedEmail}`,
                    realm: realm,
                });
            });
        } else {
            clearRaygunUser();
        }
    }, [state]);

    return null;
}

export const CloudAuthProviderLayout = (): JSXElement => {
    const [, setSearchParams] = useSearchParams();

    return (
        <CloudAuthProvider
            keycloakUrl={env.VITE_KEYCLOAK_URL}
            customerControlUrl={env.VITE_CUSTOMER_CONTROL_URL}
            onSignInCallback={() => setSearchParams({}, { replace: true })}
        >
            <AuthStateInitializer>
                <RaygunUserTracker />
                <EnvironmentProvider>
                    <Outlet />
                </EnvironmentProvider>
            </AuthStateInitializer>
        </CloudAuthProvider>
    );
};

export const RestrictedRouteLayout = (): JSXElement => {
    return (
        <RouteGuard routeType={RouteType.RESTRICTED}>
            <JobViewerLayout>
                <Suspense fallback={<Loading />}>
                    <ErrorBoundary>
                        <Outlet />
                    </ErrorBoundary>
                </Suspense>
            </JobViewerLayout>
        </RouteGuard>
    );
};
